import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, api } from '../App';

const ALL_PLATFORMS = {
  facebook:        { label: 'Facebook',  color: '#1877f2' },
  instagram:       { label: 'Instagram', color: '#e1306c' },
  linkedin:        { label: 'LinkedIn',  color: '#0077b5' },
  google_business: { label: 'Google',    color: '#ea4335' },
  x:               { label: 'X',         color: '#aaaaaa' },
  tiktok:          { label: 'TikTok',    color: '#69c9d0' },
  threads:         { label: 'Threads',   color: '#888888' },
  pinterest:       { label: 'Pinterest', color: '#e60023' },
  reddit:          { label: 'Reddit',    color: '#ff4500' },
  bluesky:         { label: 'Bluesky',   color: '#0085ff' },
  youtube:         { label: 'YouTube',   color: '#ff0000' },
};

// Video wird ueber Upload-Post's /api/upload verschickt — GBP ist dort laut
// API-Doku nicht als Zielplattform fuer Video dokumentiert.
const VIDEO_UNSUPPORTED = ['google_business', 'gbp'];

const TIKTOK_PRIVACY = [
  { value: 'PUBLIC_TO_EVERYONE',     label: 'Öffentlich' },
  { value: 'MUTUAL_FOLLOW_FRIENDS',  label: 'Nur gegenseitige Follower' },
  { value: 'SELF_ONLY',              label: 'Nur ich' },
];

function loadLS(key) { try { return localStorage.getItem(key) || ''; } catch { return ''; } }
function saveLS(key, val) { try { localStorage.setItem(key, val); } catch {} }

function Spinner() {
  return <span style={{ display: 'inline-block', width: 11, height: 11, border: '2px solid rgba(255,255,255,0.25)', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginRight: 6, verticalAlign: -1 }} />;
}

function fmtSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? mb.toFixed(1) + ' MB' : (bytes / 1024).toFixed(0) + ' KB';
}

export default function VideoPost() {
  const { token } = useAuth();
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    api('/video-posts', {}, token).then(d => setItems(d.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    api('/uploadpost/status', {}, token).then(d => { if (d.connected) { setConnectedPlatforms(d.platforms || []); setAccounts(d.accounts || []); } }).catch(() => {});
    reload();
  }, [token, reload]);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 60px' }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontFamily: 'Orbitron', fontSize: 20, margin: 0 }}>Video posten</h1>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>
          Fertiges Video hochladen, Cover hinterlegen und auf deinen verbundenen Plattformen planen oder sofort veröffentlichen.
        </p>
        {connectedPlatforms.length > 0
          ? <span style={{ color: 'var(--green)', fontSize: 12 }}>● {connectedPlatforms.length} Kanal{connectedPlatforms.length !== 1 ? 'e' : ''} verbunden</span>
          : <span style={{ color: 'var(--gold)', fontSize: 12 }}>⚠ Kein Kanal verbunden — zuerst unter Settings einrichten.</span>}
        {connectedPlatforms.some(p => VIDEO_UNSUPPORTED.includes(p)) && (
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>Hinweis: Google Business Profile unterstützt keine Video-Posts über Upload-Post und wird hier nicht angeboten.</p>
        )}
      </div>

      <UploadCard token={token} onUploaded={reload} />

      <div style={{ marginTop: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.1em', marginBottom: 10 }}>VERLAUF</div>
        {loading && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Lädt...</p>}
        {!loading && items.length === 0 && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Noch keine Videos hochgeladen.</p>}
        {items.map(item => (
          <VideoPostItem key={item.id} item={item} token={token} connectedPlatforms={connectedPlatforms.filter(p => !VIDEO_UNSUPPORTED.includes(p))} accounts={accounts} onReload={reload} />
        ))}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function UploadCard({ token, onUploaded }) {
  const videoRef = useRef(null);
  const coverRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  function pickVideo(e) {
    const f = e.target.files?.[0]; if (!f) return;
    setVideoFile(f); setVideoPreview(URL.createObjectURL(f));
  }
  function pickCover(e) {
    const f = e.target.files?.[0]; if (!f) return;
    setCoverFile(f); setCoverPreview(URL.createObjectURL(f));
  }

  async function handleUpload() {
    if (!videoFile) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('video', videoFile);
      if (coverFile) fd.append('cover', coverFile);
      const res = await fetch('/api/video-posts/upload', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload fehlgeschlagen');
      setVideoFile(null); setCoverFile(null); setVideoPreview(null); setCoverPreview(null);
      if (videoRef.current) videoRef.current.value = '';
      if (coverRef.current) coverRef.current.value = '';
      onUploaded();
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  }

  return (
    <div className="glass" style={{ padding: 18, borderRadius: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.1em', marginBottom: 10 }}>NEUES VIDEO</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Video (mp4/mov/webm, max. 300 MB)</p>
          {videoPreview
            ? <video src={videoPreview} controls style={{ width: '100%', maxHeight: 260, borderRadius: 10, background: '#000' }} />
            : <div onClick={() => videoRef.current?.click()} style={{ cursor: 'pointer', height: 140, borderRadius: 10, border: '1px dashed var(--glass-border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--muted)' }}>+ Video wählen</div>}
          <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={pickVideo} style={{ display: 'none' }} />
          {videoPreview && <button onClick={() => videoRef.current?.click()} className="btn-ghost" style={{ fontSize: 11, marginTop: 6, width: '100%' }}>Anderes Video wählen</button>}
          {videoFile && <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>{videoFile.name} · {fmtSize(videoFile.size)}</p>}
        </div>
        <div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Cover (optional, jpg/png/webp)</p>
          {coverPreview
            ? <img src={coverPreview} alt="Cover" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 10 }} />
            : <div onClick={() => coverRef.current?.click()} style={{ cursor: 'pointer', height: 140, borderRadius: 10, border: '1px dashed var(--glass-border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--muted)' }}>+ Cover wählen</div>}
          <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickCover} style={{ display: 'none' }} />
          {coverPreview && <button onClick={() => coverRef.current?.click()} className="btn-ghost" style={{ fontSize: 11, marginTop: 6, width: '100%' }}>Anderes Cover wählen</button>}
        </div>
      </div>
      {error && <p style={{ fontSize: 12, color: 'var(--red)', marginTop: 10 }}>{error}</p>}
      <button onClick={handleUpload} disabled={!videoFile || uploading} className="btn-primary" style={{ width: '100%', marginTop: 14, padding: '11px', fontSize: 13, fontWeight: 700, opacity: videoFile ? 1 : 0.4 }}>
        {uploading ? <><Spinner /> Wird hochgeladen...</> : '↑ Hochladen'}
      </button>
    </div>
  );
}

function VideoPostItem({ item, token, connectedPlatforms, accounts = [], onReload }) {
  const [open, setOpen] = useState(item.status === 'draft');
  const [videoUrl, setVideoUrl] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [title, setTitle] = useState(item.title || '');
  const [caption, setCaption] = useState(item.caption || '');
  const succeededPlatforms = (item.results || []).filter(r => r.success).map(r => r.platform);
  const remainingPlatforms = connectedPlatforms.filter(p => !succeededPlatforms.includes(p));
  const [selectedPlats, setSelectedPlats] = useState(remainingPlatforms);
  const [mediaType, setMediaType] = useState('REELS');
  // Achtung: die "username"-Kennung des verbundenen Facebook-Kontos ist die des
  // persoenlichen Profils, NICHT die Page-ID -- Facebook braucht fuer Video-Posts
  // zwingend eine Page. Deshalb kein Auto-Fill mehr, nur noch der zuletzt manuell
  // eingetragene Wert (localStorage).
  const [fbPageId, setFbPageId] = useState(() => loadLS('ct_video_fb_page_id'));
  const [pinBoardId, setPinBoardId] = useState(() => loadLS('ct_video_pinterest_board_id'));
  const [subreddit, setSubreddit] = useState(() => loadLS('ct_video_reddit_subreddit'));
  const [tiktokPrivacy, setTiktokPrivacy] = useState('PUBLIC_TO_EVERYONE');
  const ov = item.platform_overrides || {};
  const [fbDescription, setFbDescription] = useState(ov.facebook?.description || '');
  const [igTitle, setIgTitle] = useState(ov.instagram?.title || '');
  const [liTitle, setLiTitle] = useState(ov.linkedin?.title || '');
  const [liDescription, setLiDescription] = useState(ov.linkedin?.description || '');
  const [ytTitle, setYtTitle] = useState(ov.youtube?.title || '');
  const [ytDescription, setYtDescription] = useState(ov.youtube?.description || '');
  const [ytTags, setYtTags] = useState(ov.youtube?.tags || '');
  const [mode, setMode] = useState('now');
  const [scheduleMap, setScheduleMap] = useState({});
  const [quickDate, setQuickDate] = useState('');
  const [quickTime, setQuickTime] = useState('12:00');
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [videoMissing, setVideoMissing] = useState(false);

  const minDate = new Date(Date.now() + 10 * 60000).toISOString().slice(0, 10);

  useEffect(() => {
    if (!open) return;
    let revokeVideo, revokeCover;
    fetch(`/api/video-posts/${item.id}/file?type=video`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : null).then(b => { if (b) { revokeVideo = URL.createObjectURL(b); setVideoUrl(revokeVideo); } else { setVideoMissing(true); } }).catch(() => setVideoMissing(true));
    if (item.has_cover) {
      fetch(`/api/video-posts/${item.id}/file?type=cover`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.blob() : null).then(b => { if (b) { revokeCover = URL.createObjectURL(b); setCoverUrl(revokeCover); } }).catch(() => {});
    }
    return () => { if (revokeVideo) URL.revokeObjectURL(revokeVideo); if (revokeCover) URL.revokeObjectURL(revokeCover); };
  }, [open, item.id, item.has_cover, token]);

  useEffect(() => {
    setScheduleMap(prev => {
      const next = {};
      for (const pl of selectedPlats) next[pl] = prev[pl] || { date: '', time: '12:00' };
      return next;
    });
  }, [selectedPlats.join(',')]);

  function togglePlatform(id) { setSelectedPlats(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); }

  function buildPlatformOverrides() {
    return {
      facebook:  { description: fbDescription.trim() },
      instagram: { title: igTitle.trim() },
      linkedin:  { title: liTitle.trim(), description: liDescription.trim() },
      youtube:   { title: ytTitle.trim(), description: ytDescription.trim(), tags: ytTags.trim() },
    };
  }

  function saveDraftFields() {
    if (item.status !== 'draft') return;
    api(`/video-posts/${item.id}`, { method: 'PATCH', body: JSON.stringify({ title, caption, platform_overrides: buildPlatformOverrides() }) }, token).catch(() => {});
  }

  function updateFbPageId(v)  { setFbPageId(v);  saveLS('ct_video_fb_page_id', v); }
  function updatePinBoard(v)  { setPinBoardId(v); saveLS('ct_video_pinterest_board_id', v); }
  function updateSubreddit(v) { setSubreddit(v);  saveLS('ct_video_reddit_subreddit', v); }

  const missingFields = [];
  if (selectedPlats.includes('facebook')  && !fbPageId.trim())  missingFields.push('Facebook Page-ID');
  if (selectedPlats.includes('pinterest') && !pinBoardId.trim()) missingFields.push('Pinterest Board-ID');
  if (selectedPlats.includes('reddit')    && !subreddit.trim())  missingFields.push('Subreddit');

  const hasSchedule = mode === 'later' && selectedPlats.length > 0 && selectedPlats.every(pl => scheduleMap[pl]?.date);
  const canPublish  = selectedPlats.length > 0 && missingFields.length === 0 && (mode === 'now' || hasSchedule);

  async function handlePublish() {
    setPublishing(true); setError('');
    const scheduleMapForApi = mode === 'later'
      ? Object.fromEntries(selectedPlats.filter(pl => scheduleMap[pl]?.date).map(pl => [pl, `${scheduleMap[pl].date}T${scheduleMap[pl].time || '12:00'}:00`]))
      : undefined;
    const overrides = buildPlatformOverrides();
    const platformFields = {
      facebook:  { page_id: fbPageId.trim(), description: overrides.facebook.description },
      instagram: { title: overrides.instagram.title },
      linkedin:  { title: overrides.linkedin.title, description: overrides.linkedin.description },
      pinterest: { board_id: pinBoardId.trim() },
      reddit:    { subreddit: subreddit.trim().replace(/^r\//, '') },
      tiktok:    { privacy_level: tiktokPrivacy },
      youtube:   overrides.youtube,
    };
    try {
      await api(`/video-posts/${item.id}/publish`, {
        method: 'POST',
        body: JSON.stringify({ platforms: selectedPlats, caption, schedule_map: scheduleMapForApi, media_type_map: { instagram: mediaType }, platform_fields: platformFields }),
      }, token);
      onReload();
    } catch (err) { setError(err.message); }
    finally { setPublishing(false); }
  }

  async function handleDelete() {
    const msg = item.status === 'published'
      ? 'Dieser Beitrag wurde bereits veröffentlicht.\n\nAus dem Verlauf löschen? Der veröffentlichte Inhalt auf den Plattformen bleibt bestehen, aber dieser Eintrag inkl. Video/Cover ist danach unwiderruflich weg.'
      : 'Wirklich unwiderruflich löschen (inkl. Video-Datei)?';
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try { await api(`/video-posts/${item.id}`, { method: 'DELETE' }, token); onReload(); }
    catch (err) { setError(err.message); setDeleting(false); }
  }

  const statusBadge = {
    draft:     { label: 'Entwurf',        color: 'var(--muted)' },
    scheduled: { label: '📅 Geplant',      color: 'var(--cyan)' },
    published: { label: '✓ Veröffentlicht', color: 'var(--green)' },
    failed:    { label: '✕ Fehlgeschlagen', color: 'var(--red)' },
  }[item.status] || { label: item.status, color: 'var(--muted)' };

  const [cancelling, setCancelling] = useState(null);
  async function handleCancel(platform) {
    setCancelling(platform);
    try { await api(`/video-posts/${item.id}/cancel`, { method: 'POST', body: JSON.stringify({ platform }) }, token); onReload(); }
    catch (err) { setError(err.message); }
    finally { setCancelling(null); }
  }

  return (
    <div className="glass fade-in" style={{ borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }} className="truncate">{item.title || 'Ohne Titel'}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(item.created_at).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: statusBadge.color, flexShrink: 0, marginLeft: 10 }}>{statusBadge.label}</span>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--glass-border)' }}>
          <div style={{ display: 'flex', gap: 12, padding: '14px 0', flexWrap: 'wrap' }}>
            {videoUrl && <video src={videoUrl} controls onError={() => { setVideoUrl(null); setVideoMissing(true); }} style={{ width: 160, maxHeight: 240, borderRadius: 8, background: '#000' }} />}
            {coverUrl && <img src={coverUrl} alt="Cover" onError={() => setCoverUrl(null)} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }} />}
            {videoMissing && <p style={{ fontSize: 11, color: 'var(--muted)' }}>Video nicht mehr verfügbar (Datei entfernt).</p>}
          </div>

          <input value={title} onChange={e => setTitle(e.target.value)} onBlur={saveDraftFields} placeholder="Titel (intern, nur für den Verlauf)" className="input" style={{ marginBottom: 8, fontSize: 13 }} />
          <textarea value={caption} onChange={e => setCaption(e.target.value)} onBlur={saveDraftFields} placeholder="Caption / Text für den Post..." rows={4} className="input" style={{ fontSize: 13, resize: 'vertical', marginBottom: 14 }} />

          {(item.results || []).length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {item.results.map(r => {
                const pl = ALL_PLATFORMS[r.platform] || { label: r.platform, color: '#888' };
                const schedTime = r.scheduled && r.schedule_at ? new Date(r.schedule_at).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
                return <div key={r.platform} style={{ marginRight: 8, marginBottom: 8, display: 'inline-block', verticalAlign: 'top' }}>
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: r.success ? `${pl.color}18` : r.skipped ? 'var(--glass2)' : 'var(--red-dim)', color: r.success ? pl.color : r.skipped ? 'var(--muted)' : 'var(--red)', border: `1px solid ${r.success ? pl.color + '40' : r.skipped ? 'var(--glass-border)' : 'rgba(255,0,85,0.3)'}` }}>{r.success ? (r.scheduled ? '📅 ' : '✓ ') : r.skipped ? '— ' : '✕ '}{pl.label}</span>
                  {!r.success && (r.reason || r.error) && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3, maxWidth: 220 }}>{r.reason || r.error}</div>}
                  {schedTime && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{schedTime} <button onClick={() => handleCancel(r.platform)} disabled={cancelling === r.platform} className="btn-ghost" style={{ fontSize: 10, padding: '1px 6px', marginLeft: 4 }}>{cancelling === r.platform ? '...' : 'stornieren'}</button></div>}
                </div>;
              })}
              {item.post_url && <p style={{ fontSize: 12, marginTop: 2 }}><a href={item.post_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>Post ansehen →</a></p>}
            </div>
          )}

          {remainingPlatforms.length > 0 ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>{succeededPlatforms.length > 0 ? 'WEITERE PLATTFORMEN NACHHOLEN' : 'PLATTFORMEN'}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {remainingPlatforms.map(id => {
                    const pl = ALL_PLATFORMS[id] || { label: id, color: '#888' };
                    const active = selectedPlats.includes(id);
                    return <button key={id} onClick={() => togglePlatform(id)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', background: active ? `${pl.color}18` : 'var(--glass)', color: active ? pl.color : 'var(--muted)', border: `1px solid ${active ? pl.color + '55' : 'var(--glass-border)'}`, fontWeight: active ? 700 : 400 }}>{active ? '✓ ' : ''}{pl.label}</button>;
                  })}
                </div>
                {selectedPlats.includes('instagram') && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>Instagram-Format</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['REELS', 'STORIES'].map(t => (
                        <button key={t} onClick={() => setMediaType(t)} style={{ padding: '5px 12px', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: mediaType === t ? 'var(--cyan-dim)' : 'var(--glass)', color: mediaType === t ? 'var(--cyan)' : 'var(--muted)', border: `1px solid ${mediaType === t ? 'rgba(0,255,255,0.3)' : 'var(--glass-border)'}` }}>{t === 'REELS' ? 'Reel' : 'Story'}</button>
                      ))}
                    </div>
                    <textarea value={igTitle} onChange={e => setIgTitle(e.target.value)} onBlur={saveDraftFields} placeholder="Eigene Instagram-Caption (optional, sonst gemeinsame Caption oben)" rows={2} className="input" style={{ fontSize: 12, padding: '7px 10px', resize: 'vertical', marginTop: 8 }} />
                  </div>
                )}

                {selectedPlats.includes('facebook') && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>Facebook Page-ID <span style={{ color: 'var(--red)' }}>*</span> (Pflicht, sonst kein Post möglich — nicht deine persönliche Profil-ID, sondern die ID der Facebook-Seite/Page. Steht in den Facebook-Seiteneinstellungen unter "Info", oder erscheint bei einem Fehlversuch als "Accessible pages" in der Fehlermeldung)</div>
                    <input value={fbPageId} onChange={e => updateFbPageId(e.target.value)} placeholder="z.B. 123456789012345" className="input" style={{ fontSize: 12, padding: '7px 10px' }} />
                    <textarea value={fbDescription} onChange={e => setFbDescription(e.target.value)} onBlur={saveDraftFields} placeholder="Eigene Facebook-Beschreibung (optional, sonst gemeinsame Caption oben)" rows={2} className="input" style={{ fontSize: 12, padding: '7px 10px', resize: 'vertical', marginTop: 8 }} />
                  </div>
                )}

                {selectedPlats.includes('linkedin') && (
                  <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--glass)', borderRadius: 8, border: '1px solid var(--glass-border)' }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>LinkedIn-Details (optional, sachlicherer Ton laut Plan, sonst gemeinsame Caption)</div>
                    <input value={liTitle} onChange={e => setLiTitle(e.target.value)} onBlur={saveDraftFields} placeholder="LinkedIn-Titel" className="input" style={{ fontSize: 12, padding: '7px 10px', marginBottom: 6 }} />
                    <textarea value={liDescription} onChange={e => setLiDescription(e.target.value)} onBlur={saveDraftFields} placeholder="LinkedIn-Text (Commentary)" rows={3} className="input" style={{ fontSize: 12, padding: '7px 10px', resize: 'vertical' }} />
                  </div>
                )}

                {selectedPlats.includes('pinterest') && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>Pinterest Board-ID <span style={{ color: 'var(--red)' }}>*</span> (Pflicht)</div>
                    <input value={pinBoardId} onChange={e => updatePinBoard(e.target.value)} placeholder="Board-ID" className="input" style={{ fontSize: 12, padding: '7px 10px' }} />
                  </div>
                )}

                {selectedPlats.includes('reddit') && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>Subreddit <span style={{ color: 'var(--red)' }}>*</span> (ohne r/, Pflicht)</div>
                    <input value={subreddit} onChange={e => updateSubreddit(e.target.value)} placeholder="z.B. smallbusiness" className="input" style={{ fontSize: 12, padding: '7px 10px' }} />
                  </div>
                )}

                {selectedPlats.includes('tiktok') && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>TikTok-Sichtbarkeit</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {TIKTOK_PRIVACY.map(o => (
                        <button key={o.value} onClick={() => setTiktokPrivacy(o.value)} style={{ padding: '5px 12px', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: tiktokPrivacy === o.value ? 'var(--cyan-dim)' : 'var(--glass)', color: tiktokPrivacy === o.value ? 'var(--cyan)' : 'var(--muted)', border: `1px solid ${tiktokPrivacy === o.value ? 'rgba(0,255,255,0.3)' : 'var(--glass-border)'}` }}>{o.label}</button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedPlats.includes('youtube') && (
                  <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--glass)', borderRadius: 8, border: '1px solid var(--glass-border)' }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>YouTube-Details (optional, sonst wird die Caption als Titel verwendet)</div>
                    <input value={ytTitle} onChange={e => setYtTitle(e.target.value)} placeholder="YouTube-Titel" className="input" style={{ fontSize: 12, padding: '7px 10px', marginBottom: 6 }} />
                    <textarea value={ytDescription} onChange={e => setYtDescription(e.target.value)} placeholder="YouTube-Beschreibung" rows={2} className="input" style={{ fontSize: 12, padding: '7px 10px', resize: 'vertical', marginBottom: 6 }} />
                    <input value={ytTags} onChange={e => setYtTags(e.target.value)} placeholder="Tags, kommagetrennt (z.B. Handwerk, SEO, Neustrelitz)" className="input" style={{ fontSize: 12, padding: '7px 10px' }} />
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>WANN</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[['now', '⊹', 'Jetzt sofort', 'var(--green-dim)', 'rgba(0,255,136,0.5)', 'var(--green)'], ['later', '📅', 'Geplant', 'var(--cyan-dim)', 'rgba(0,229,255,0.5)', 'var(--cyan)']].map(([m, ic, t, bg, bc, c]) => (
                    <button key={m} onClick={() => setMode(m)} style={{ padding: '9px 14px', borderRadius: 10, cursor: 'pointer', background: mode === m ? bg : 'var(--glass)', border: `2px solid ${mode === m ? bc : 'var(--glass-border)'}`, color: mode === m ? c : 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}><span>{ic}</span><span style={{ fontSize: 13, fontWeight: 600 }}>{t}</span></button>
                  ))}
                </div>
                {mode === 'later' && selectedPlats.length > 0 && (
                  <div className="fade-in" style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', padding: '10px 12px', background: 'var(--glass)', borderRadius: '9px 9px 0 0', border: '1px solid var(--glass-border2)', borderBottom: 'none' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>FÜR ALLE</div>
                        <input type="date" value={quickDate} onChange={e => setQuickDate(e.target.value)} min={minDate} style={{ width: '100%', padding: '7px 9px', background: 'var(--glass2)', border: '1px solid var(--glass-border2)', borderRadius: 7, color: 'var(--text)', fontSize: 12, boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ width: 110 }}>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>UHRZEIT</div>
                        <input type="time" value={quickTime} onChange={e => setQuickTime(e.target.value)} style={{ width: '100%', padding: '7px 9px', background: 'var(--glass2)', border: '1px solid var(--glass-border2)', borderRadius: 7, color: 'var(--text)', fontSize: 12, boxSizing: 'border-box' }} />
                      </div>
                      <button onClick={() => { if (!quickDate) return; setScheduleMap(prev => { const next = { ...prev }; for (const pl of selectedPlats) next[pl] = { date: quickDate, time: quickTime }; return next; }); }} disabled={!quickDate} className="btn-ghost" style={{ fontSize: 11, padding: '7px 12px', flexShrink: 0, opacity: quickDate ? 1 : 0.4 }}>→ Alle</button>
                    </div>
                    <div style={{ padding: '8px 12px 4px', background: 'var(--glass)', border: '1px solid var(--glass-border2)', borderTop: '1px solid var(--glass-border)', borderRadius: '0 0 9px 9px' }}>
                      {selectedPlats.map(id => {
                        const pl = ALL_PLATFORMS[id] || { label: id, color: '#888' };
                        const entry = scheduleMap[id] || { date: '', time: '12:00' };
                        return (
                          <div key={id} style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ width: 88, fontSize: 11, color: pl.color, fontWeight: 600, flexShrink: 0 }} className="truncate">{pl.label}</div>
                            <input type="date" value={entry.date} onChange={e => setScheduleMap(prev => ({ ...prev, [id]: { ...prev[id], date: e.target.value } }))} min={minDate} style={{ flex: 1, padding: '6px 8px', background: 'var(--glass2)', border: `1px solid ${entry.date ? pl.color + '50' : 'var(--glass-border2)'}`, borderRadius: 7, color: 'var(--text)', fontSize: 12, boxSizing: 'border-box' }} />
                            <input type="time" value={entry.time || '12:00'} onChange={e => setScheduleMap(prev => ({ ...prev, [id]: { ...prev[id], time: e.target.value } }))} style={{ width: 90, padding: '6px 8px', background: 'var(--glass2)', border: `1px solid ${entry.date ? pl.color + '50' : 'var(--glass-border2)'}`, borderRadius: 7, color: 'var(--text)', fontSize: 12, boxSizing: 'border-box' }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {missingFields.length > 0 && <p style={{ fontSize: 12, color: 'var(--gold)', marginBottom: 10 }}>⚠ Fehlt noch: {missingFields.join(', ')}</p>}
              {error && <p style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handlePublish} disabled={publishing || !canPublish} className="btn-primary" style={{ flex: 1, padding: '11px', fontSize: 13, fontWeight: 700, opacity: canPublish ? 1 : 0.4 }}>
                  {publishing ? <><Spinner /> Wird veröffentlicht...</> : hasSchedule ? `📅 Planen — ${selectedPlats.length} Plattform${selectedPlats.length !== 1 ? 'en' : ''}` : item.status === 'failed' ? `↻ Erneut versuchen — ${selectedPlats.length} Plattform${selectedPlats.length !== 1 ? 'en' : ''}` : `⊹ Jetzt veröffentlichen — ${selectedPlats.length} Plattform${selectedPlats.length !== 1 ? 'en' : ''}`}
                </button>
                <button onClick={handleDelete} disabled={deleting} className="btn-ghost" style={{ fontSize: 12 }}>{deleting ? '...' : '🗑'}</button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleDelete} disabled={deleting} className="btn-ghost" style={{ fontSize: 11 }}>{deleting ? '...' : 'Aus Verlauf entfernen'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
